import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useCountries } from "@/lib/countries";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function CompaniesPage() {
  const countries = useCountries();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", country: "United Kingdom", timezone: "Europe/London" });

  const load = async () => {
    const { data } = await api.get("/companies");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies", form);
      toast.success("Company created");
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const toggle = async (c) => {
    try {
      await api.patch(`/companies/${c.company_id}`, { is_active: !c.is_active });
      toast.success(c.is_active ? "Company disabled" : "Company enabled");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow">Platform</div>
          <h1 className="font-display text-4xl mt-1">Companies</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-company-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              <Plus className="h-4 w-4 mr-1"/>New company
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create company</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="eyebrow">Name</Label>
                <Input required data-testid="company-create-name" value={form.name}
                  onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-2 h-11 rounded-lg"/>
              </div>
              <div>
                <Label className="eyebrow">Country</Label>
                <Select value={form.country} onValueChange={(v) => {
                  const c = countries.find(x => x.name === v);
                  setForm({ ...form, country: v, timezone: c?.timezone || form.timezone });
                }}>
                  <SelectTrigger data-testid="company-create-country" className="mt-2 h-11 rounded-lg"><SelectValue placeholder="Select country"/></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {countries.map(c => <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="eyebrow">Timezone</Label>
                <Input value={form.timezone}
                  onChange={(e)=>setForm({...form, timezone:e.target.value})} className="mt-2 h-11 rounded-lg"/>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="company-create-submit" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#4B5773]">
              <th className="py-3 eyebrow font-normal">Company</th>
              <th className="py-3 eyebrow font-normal">Region</th>
              <th className="py-3 eyebrow font-normal">Status</th>
              <th className="py-3 eyebrow font-normal text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(c => (
              <tr key={c.company_id}>
                <td className="py-3 font-medium">{c.name}</td>
                <td className="py-3">{c.country} <span className="text-[#4B5773]">· {c.timezone}</span></td>
                <td className="py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.is_active ? "bg-[#DBE7FE] text-[#1D4ED8]" : "bg-slate-100 text-slate-500"}`}>
                    {c.is_active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={()=>toggle(c)} data-testid={`toggle-${c.company_id}`}>
                    {c.is_active ? "Disable" : "Enable"}
                  </Button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className="py-8 text-center text-[#4B5773]">No companies yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
